/**
 * What the panel last learned of a row from the SERVER (a fetch, or the answer
 * to its own save). It is the `base` of the conflict guard: when a field is
 * saved, the value the server held for it, as far as this tab knows.
 *
 * Deliberately separate from the query cache: optimistic updates write the
 * user's own edit there, and an edit is not something the server confirmed.
 * Nothing here is ever written from a local edit.
 */

export type Fields = Record<string, unknown>;

export class KnownRows {
  private readonly rows = new Map<string, Fields>();

  /** Server values arrived (fetch or save response): they replace what we knew, field by field. */
  seed(id: string, row: Fields): void {
    const prev = this.rows.get(id);
    this.rows.set(id, prev ? { ...prev, ...row } : { ...row });
  }

  /** The server stored these values (answer to a save). */
  confirm(id: string, values: Fields): void {
    this.seed(id, values);
  }

  has(id: string): boolean {
    return this.rows.has(id);
  }

  /** The row is gone or must be re-read: forget it so the next save runs without `base`. */
  forget(id: string): void {
    this.rows.delete(id);
  }

  clear(): void {
    this.rows.clear();
  }

  /**
   * `base` for a patch: for each patched field the server value we know.
   * Fields never seen are left out (that field saves last-write-wins).
   */
  base(id: string, keys: readonly string[], ignore: readonly string[] = []): Fields {
    const known = this.rows.get(id);
    const out: Fields = {};
    if (!known) return out;
    for (const key of keys) {
      if (key === 'base' || ignore.includes(key)) continue;
      if (Object.hasOwn(known, key)) out[key] = known[key] ?? null;
    }
    return out;
  }
}

/** One store per kind of row the conflict guard covers. */
export const knownRows = {
  tour: new KnownRows(),
  panorama: new KnownRows(),
  marker: new KnownRows(),
};
